/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */

/**
 * The types for table Expenses Analyzer
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @version 1.2.0
 * @since 1.1.0
 */

import {
    ColumnFieldType,
    TableFooterColumnFieldType,
} from '../../../../../components/Table/types/columnTypes';

export enum EExpensesAnalyzerField {
    ACCOUNT = 'account',
    AMOUNT_COUNTRY_ROAMING = 'amountCountryRoaming',
    AMOUNT_LOCAL = 'amountLocal',
    AMOUNT_LOCAL_MACRO = 'amountLocalMacro',
    ACCOUNT_REGION = 'accountRegion',
    AMOUNT_ROAMING = 'amountRouming',
    AMOUNT_TOTAL = 'amountTotal',
    BIS_ACCOUNT_ID = 'bisAccountId',
    CHARGES = 'charges',
    DETAILS = 'details',
    MONTH_CHARGE_RTPL = 'monthChargeRTPL',
    MONTH_CHARGE_SRLS = 'monthChargeSRLS',
    MONTH_RANGE = 'monthRange',
    MSISDN = 'msisdn',
    NAME = 'name',
}

export interface IExpensesAnalyzerDataDetails {
    [EExpensesAnalyzerField.CHARGES]: number;
    [EExpensesAnalyzerField.MONTH_CHARGE_SRLS]: number;
    [EExpensesAnalyzerField.MONTH_CHARGE_RTPL]: number;
    [EExpensesAnalyzerField.AMOUNT_TOTAL]: number;
    [EExpensesAnalyzerField.AMOUNT_LOCAL_MACRO]: number;
    [EExpensesAnalyzerField.AMOUNT_LOCAL]: number;
    [EExpensesAnalyzerField.AMOUNT_COUNTRY_ROAMING]: number;
    [EExpensesAnalyzerField.AMOUNT_ROAMING]: number;
    month: string;
}

export interface IExpensesAnalyzerData {
    [EExpensesAnalyzerField.MSISDN]: string;
    [EExpensesAnalyzerField.NAME]: string;
    [EExpensesAnalyzerField.BIS_ACCOUNT_ID]: number;
    [EExpensesAnalyzerField.ACCOUNT]: string;
    [EExpensesAnalyzerField.ACCOUNT_REGION]: string;
    [EExpensesAnalyzerField.CHARGES]: number;
    [EExpensesAnalyzerField.MONTH_CHARGE_SRLS]: number;
    [EExpensesAnalyzerField.MONTH_CHARGE_RTPL]: number;
    [EExpensesAnalyzerField.MONTH_RANGE]: string;
    [EExpensesAnalyzerField.AMOUNT_TOTAL]: number;
    [EExpensesAnalyzerField.AMOUNT_LOCAL_MACRO]: number;
    [EExpensesAnalyzerField.AMOUNT_LOCAL]: number;
    [EExpensesAnalyzerField.AMOUNT_COUNTRY_ROAMING]: number;
    [EExpensesAnalyzerField.AMOUNT_ROAMING]: number;
    [EExpensesAnalyzerField.DETAILS]: IExpensesAnalyzerDataDetails[];
}

export const ExpensesAnalyzerColumnNames: EExpensesAnalyzerField[] = [
    EExpensesAnalyzerField.MSISDN,
    EExpensesAnalyzerField.ACCOUNT,
    EExpensesAnalyzerField.NAME,
    EExpensesAnalyzerField.CHARGES,
    EExpensesAnalyzerField.MONTH_CHARGE_SRLS,
    EExpensesAnalyzerField.MONTH_CHARGE_RTPL,
    EExpensesAnalyzerField.AMOUNT_TOTAL,
    EExpensesAnalyzerField.AMOUNT_LOCAL_MACRO,
    EExpensesAnalyzerField.AMOUNT_LOCAL,
    EExpensesAnalyzerField.AMOUNT_COUNTRY_ROAMING,
    EExpensesAnalyzerField.AMOUNT_ROAMING,
];

export enum EExpensesAnalyzerTableFooterField {
    CHARGES = 'charges',
    MONTH_CHARGE_SRLS = 'monthChargeSRLS',
    MONTH_CHARGE_RTPL = 'monthChargeRTPL',
    AMOUNT_TOTAL = 'amountTotal',
    AMOUNT_LOCAL_MACRO = 'amountLocalMacro',
    AMOUNT_LOCAL = 'amountLocal',
    AMOUNT_COUNTRY_ROAMING = 'amountCountryRoaming',
    AMOUNT_ROAMING = 'amountRouming',
}

export interface IExpensesAnalyzerTableFooter {
    [EExpensesAnalyzerTableFooterField.CHARGES]: number;
    [EExpensesAnalyzerTableFooterField.MONTH_CHARGE_SRLS]: number;
    [EExpensesAnalyzerTableFooterField.MONTH_CHARGE_RTPL]: number;
    [EExpensesAnalyzerTableFooterField.AMOUNT_TOTAL]: number;
    [EExpensesAnalyzerTableFooterField.AMOUNT_LOCAL_MACRO]: number;
    [EExpensesAnalyzerTableFooterField.AMOUNT_LOCAL]: number;
    [EExpensesAnalyzerTableFooterField.AMOUNT_COUNTRY_ROAMING]: number;
    [EExpensesAnalyzerTableFooterField.AMOUNT_ROAMING]: number;
}

export const MapExpensesAnalyzerFieldToFooter: { [key in ColumnFieldType]?: TableFooterColumnFieldType } = {
    [EExpensesAnalyzerField.CHARGES]: EExpensesAnalyzerTableFooterField.CHARGES,
    [EExpensesAnalyzerField.MONTH_CHARGE_SRLS]: EExpensesAnalyzerTableFooterField.MONTH_CHARGE_SRLS,
    [EExpensesAnalyzerField.MONTH_CHARGE_RTPL]: EExpensesAnalyzerTableFooterField.MONTH_CHARGE_RTPL,
    [EExpensesAnalyzerField.AMOUNT_TOTAL]: EExpensesAnalyzerTableFooterField.AMOUNT_TOTAL,
    [EExpensesAnalyzerField.AMOUNT_LOCAL_MACRO]: EExpensesAnalyzerTableFooterField.AMOUNT_LOCAL_MACRO,
    [EExpensesAnalyzerField.AMOUNT_LOCAL]: EExpensesAnalyzerTableFooterField.AMOUNT_LOCAL,
    [EExpensesAnalyzerField.AMOUNT_COUNTRY_ROAMING]: EExpensesAnalyzerTableFooterField.AMOUNT_COUNTRY_ROAMING,
    [EExpensesAnalyzerField.AMOUNT_ROAMING]: EExpensesAnalyzerTableFooterField.AMOUNT_ROAMING,
};
